import React, { FC, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import SocialsList from 'shared/SocialsList/SocialsList';
import Label from 'components/Label/Label';
import Input from 'shared/Input/Input';
import Textarea from 'shared/Textarea/Textarea';
import ButtonPrimary from 'shared/Button/ButtonPrimary';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FormFeedback } from 'reactstrap';
import axios from 'service/axios';
import { toast } from 'react-toastify';

export interface PageContactProps {
    className?: string;
}

const info = [
    {
        title: '🗺 ADDRESS',
        desc: 'Ahmedabad, Gujarat, India'
    },
    {
        title: '💌 EMAIL',
        desc: 'Support@Instaacard.com'
    },
    {
        title: '☎ PHONE',
        desc: '+91 70697 91000'
    }
];

// Mirrors the server-side rules on POST /api/v1/client/contact-us
const validationScheme = yup.object().shape({
    firstName: yup.string().required('First name is required.').trim(),
    lastName: yup.string().required('Last name is required.').trim(),
    email: yup.string().required('Email is required.').email('Must be a valid email.').trim(),
    mobile: yup
        .string()
        .required('Mobile number is required.')
        .matches(/^[0-9+\-\s()]{10,15}$/, 'Must be a valid mobile number.')
        .trim(),
    messageContent: yup.string().required('Message is required.').trim()
});

const PageContact: FC<PageContactProps> = ({ className = '' }) => {
    const [submitting, setSubmitting] = useState<boolean>(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm({
        mode: 'onChange',
        resolver: yupResolver(validationScheme)
    });

    const onSubmit = async (formData: any) => {
        setSubmitting(true);
        try {
            const res = await axios.post('/contact-us', { ...formData, source: 'website' });
            toast.success(res?.data?.message || 'Thank you for getting in touch. We will get back to you shortly.');
            reset();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={`nc-PageContact overflow-hidden ${className}`} data-nc-id="PageContact">
            <Helmet>
                <title>Contact | Instaacard</title>
            </Helmet>
            <div className="">
                <h2 className="my-20 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
                    Contact
                </h2>
                <div className="container max-w-7xl mx-auto">
                    <div className="flex-shrink-0 grid grid-cols-1 md:grid-cols-2 gap-12 ">
                        <div className="max-w-sm space-y-8">
                            {info.map((item, index) => (
                                <div key={index}>
                                    <h3 className="uppercase font-semibold text-sm dark:text-neutral-200 tracking-wider">{item.title}</h3>
                                    <span className="block mt-2 text-neutral-500 dark:text-neutral-400">{item.desc}</span>
                                </div>
                            ))}
                            <div>
                                <h3 className="uppercase font-semibold text-sm dark:text-neutral-200 tracking-wider">🌏 SOCIALS</h3>
                                <SocialsList className="mt-2" />
                            </div>
                        </div>
                        <div>
                            <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <label className="block">
                                        <Label>First name</Label>
                                        <Input
                                            placeholder="Example"
                                            type="text"
                                            className={`mt-1 ${errors.firstName ? '!border-red-500' : ''}`}
                                            {...register('firstName')}
                                        />
                                        {errors.firstName && <FormFeedback className="d-block">{errors.firstName.message}</FormFeedback>}
                                    </label>
                                    <label className="block">
                                        <Label>Last name</Label>
                                        <Input
                                            placeholder="Doe"
                                            type="text"
                                            className={`mt-1 ${errors.lastName ? '!border-red-500' : ''}`}
                                            {...register('lastName')}
                                        />
                                        {errors.lastName && <FormFeedback className="d-block">{errors.lastName.message}</FormFeedback>}
                                    </label>
                                </div>
                                <label className="block">
                                    <Label>Email address</Label>
                                    <Input
                                        type="email"
                                        placeholder="example@example.com"
                                        className={`mt-1 ${errors.email ? '!border-red-500' : ''}`}
                                        {...register('email')}
                                    />
                                    {errors.email && <FormFeedback className="d-block">{errors.email.message}</FormFeedback>}
                                </label>
                                <label className="block">
                                    <Label>Mobile number</Label>
                                    <Input
                                        type="tel"
                                        placeholder="+91 00000 00000"
                                        className={`mt-1 ${errors.mobile ? '!border-red-500' : ''}`}
                                        {...register('mobile')}
                                    />
                                    {errors.mobile && <FormFeedback className="d-block">{errors.mobile.message}</FormFeedback>}
                                </label>
                                <label className="block">
                                    <Label>Message</Label>
                                    <Textarea className="mt-1" rows={6} {...register('messageContent')} />
                                    {errors.messageContent && (
                                        <FormFeedback className="d-block">{errors.messageContent.message}</FormFeedback>
                                    )}
                                </label>
                                <div>
                                    <ButtonPrimary type="submit" disabled={submitting}>
                                        {submitting ? 'Sending...' : 'Send Message'}
                                    </ButtonPrimary>
                                </div>
                                <br />
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PageContact;
